/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

import { useState } from 'react';
import { createPortal } from 'react-dom';
import toast from 'react-hot-toast';
import { XMarkIcon, UserPlusIcon, EnvelopeIcon, ShieldCheckIcon, EyeIcon, CurrencyDollarIcon } from '@heroicons/react/24/outline';
import { organizationService } from '../../services/api';
import { OrgRole } from '../../types';

const ROLE_OPTIONS: { value: OrgRole; label: string; hint: string; icon: typeof EyeIcon }[] = [
  { value: 'org_member', label: 'Member', hint: 'Access org projects they are added to', icon: EyeIcon },
  { value: 'org_admin', label: 'Admin', hint: 'Manage members, domains, SSO and org projects', icon: ShieldCheckIcon },
  { value: 'org_billing_admin', label: 'Billing admin', hint: 'Manage org billing only', icon: CurrencyDollarIcon },
];

export default function InviteOrgMemberModal({ orgId, onClose, onInvited }: { orgId: string; onClose: () => void; onInvited: () => void }) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<OrgRole>('org_member');
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSending(true);
    try {
      await organizationService.invite(orgId, email, role);
      toast.success(`Invitation sent to ${email}`);
      onInvited();
      onClose();
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Failed to send invitation');
    } finally {
      setSending(false);
    }
  };

  const modalContent = (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div
        className="bg-gray-900 border border-gray-800 rounded-2xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-800">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary-500/20 rounded-lg">
              <UserPlusIcon className="w-6 h-6 text-primary-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">Invite to organisation</h3>
              <p className="text-sm text-gray-400">Send an invitation to join this org</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-gray-600 hover:text-gray-400 hover:bg-gray-800 rounded-lg">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Email Address</label>
            <div className="relative">
              <EnvelopeIcon className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input pl-10"
                placeholder="colleague@example.com"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Role</label>
            <div className="space-y-2">
              {ROLE_OPTIONS.map((o) => {
                const Icon = o.icon;
                return (
                  <button
                    key={o.value}
                    type="button"
                    onClick={() => setRole(o.value)}
                    className={`w-full p-3 border-2 rounded-xl text-left transition-colors ${
                      role === o.value ? 'border-primary-500 bg-primary-500/10' : 'border-gray-700 hover:border-gray-600'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-0.5">
                      <Icon className="w-4 h-4 text-primary-400" />
                      <span className="font-medium text-white text-sm">{o.label}</span>
                    </div>
                    <p className="text-xs text-gray-400">{o.hint}</p>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 btn-secondary">
              Cancel
            </button>
            <button type="submit" disabled={sending} className="flex-1 btn-primary disabled:opacity-50">
              {sending ? 'Sending…' : 'Send invitation'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
